// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Component for selecting dates in report interfaces.
 *
 * Provides date validation to ensure only valid dates are passed to API functions.
 * Handles timezone display and prevents invalid date submissions.
 * Supports optional date shortcuts for common financial reporting periods.
 */
import { TooltipTypography } from '@/components/tooltip-typography';
import { DateShortcut, MINIMUM_DATE } from '@/constants/date-shortcut';
import { getToday } from '@/utilities/date-time';
import { Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { DateTime, IANAZone } from 'luxon';
import { PropsWithChildren } from 'react';

interface Props {
  date: DateTime;
  timezone: IANAZone;
  onChange: (date: DateTime | null) => void;
  /**
   * Optional collection of date shortcuts to display.
   * If not provided, no shortcuts will be shown.
   */
  shortcuts?: DateShortcut[];
  /**
   * If true, future dates are disabled and cannot be selected.
   * Useful for historical reports like inventory snapshots.
   */
  disableFuture?: boolean;
}

export function ReportDateInput(props: PropsWithChildren<Props>) {
  /**
   * Handles date changes with validation to prevent invalid dates from being passed up.
   * This ensures the component layer validates dates before they reach API functions.
   */
  const handleDateChange = (date: DateTime | null) => {
    // Only call onChange if the date is valid or null
    if (date === null || date.isValid) {
      props.onChange(date);
    }
    // Invalid dates are silently ignored to prevent API errors
  };

  // Convert date shortcuts to the format expected by MUI DatePicker
  const today = getToday();
  const shortcuts = props.shortcuts?.map(({ label, getDate }) => ({
    label,
    getValue: () => getDate(today),
  }));

  return (
    <Stack spacing={1} alignItems="end">
      <Stack direction="row" alignItems="center" spacing={1}>
        {props.children}
        <DatePicker
          label="As of date"
          format="yyyy-MM-dd"
          value={props.date}
          onChange={handleDateChange}
          disableFuture={props.disableFuture}
          minDate={MINIMUM_DATE}
          sx={{ width: '200px' }}
          slotProps={{
            textField: { size: 'small' },
            shortcuts: { items: shortcuts },
          }}
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ paddingTop: 0, marginTop: 0 }}
      >
        Reported for{' '}
        <TooltipTypography
          variant="inherit"
          component="span"
          tooltip="Determined based on your organization's time zone setting"
        >
          {props.timezone.name}
        </TooltipTypography>
      </Typography>
    </Stack>
  );
}
